import React, { useState, useEffect } from 'react';
import { FileText, AlertTriangle, ChevronDown, ChevronRight, Shield } from 'lucide-react';
import { piApi } from '../../../services/piApi';
import '../PIShell.css';

const SEVERITY_CONFIG = {
  CRITICAL: { cls: 'pi-badge-red',   label: 'Critical' },
  HIGH:     { cls: 'pi-badge-red',   label: 'High' },
  MEDIUM:   { cls: 'pi-badge-amber', label: 'Medium' },
  LOW:      { cls: 'pi-badge-green', label: 'Low' },
};

function SeveritySummary({ report }) {
  if (!report?.by_severity) return null;
  const { critical, high, medium, low } = report.by_severity;
  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      {critical > 0 && <span className="pi-badge pi-badge-red">{critical} Critical</span>}
      {high > 0 && <span className="pi-badge pi-badge-red">{high} High</span>}
      {medium > 0 && <span className="pi-badge pi-badge-amber">{medium} Medium</span>}
      {low > 0 && <span className="pi-badge pi-badge-green">{low} Low</span>}
    </div>
  );
}

function FlagCard({ flag }) {
  const [expanded, setExpanded] = useState(false);
  const sev = SEVERITY_CONFIG[flag.severity] || SEVERITY_CONFIG.MEDIUM;

  return (
    <div className="pi-card" style={{ marginBottom: 8, cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span className={`pi-badge ${sev.cls}`} style={{ fontSize: 10 }}>{sev.label}</span>
        <span style={{ fontSize: 12, color: '#64748b' }}>{flag.category}</span>
        <span style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{flag.title}</span>
        {flag.id && <span style={{ fontSize: 10, color: '#94a3b8' }}>{flag.id}</span>}
      </div>
      {expanded && (
        <div style={{ marginTop: 10, paddingLeft: 22 }}>
          {flag.detail && <p style={{ fontSize: 12, color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{flag.detail}</p>}
          {flag.action_required && (
            <div style={{ marginTop: 8, padding: '6px 10px', background: '#fef3c7', borderRadius: 4, fontSize: 12 }}>
              <strong>Action Required:</strong> {flag.action_required}
            </div>
          )}
          {flag.financial_exposure && (
            <div style={{ marginTop: 4, fontSize: 11, color: '#dc2626' }}>
              Financial Exposure: {flag.financial_exposure}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function DDReportSection({ report }) {
  if (!report) return <div className="pi-empty-sub">No DD report available for this portfolio company.</div>;

  const execSummary = report.executive_summary ||
    (Array.isArray(report.executive_summary_paragraphs) ? report.executive_summary_paragraphs.join('\n\n') : null);
  const snapshot = report.financial_snapshot || [];
  const docsReviewed = report.documents_reviewed || [];
  const nextSteps = report.next_steps || [];

  return (
    <div>
      {report.deal_grade && (
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Shield size={16} />
          <span style={{ fontSize: 14, fontWeight: 700 }}>Deal Grade: {report.deal_grade}</span>
          {report.status && <span className="pi-badge pi-badge-blue" style={{ fontSize: 10 }}>{report.status}</span>}
        </div>
      )}

      {execSummary && (
        <div className="pi-card" style={{ marginBottom: 16 }}>
          <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Executive Summary</h4>
          <p style={{ fontSize: 12, color: '#334155', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{execSummary}</p>
        </div>
      )}

      {snapshot.length > 0 && (
        <div className="pi-card" style={{ marginBottom: 16 }}>
          <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Financial Snapshot</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
            {snapshot.map((s, i) => (
              <div key={i} className="pi-stat-card">
                <div className="pi-stat-value" style={{ fontSize: 16 }}>{s.value}</div>
                <div className="pi-stat-label">{s.metric}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {nextSteps.length > 0 && (
        <div className="pi-card" style={{ marginBottom: 16 }}>
          <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Next Steps from DD</h4>
          {nextSteps.map((ns, i) => (
            <div key={i} style={{ marginBottom: 8 }}>
              <span className="pi-badge pi-badge-amber" style={{ fontSize: 10, marginRight: 6 }}>{ns.severity}</span>
              <span style={{ fontSize: 12, fontWeight: 600 }}>{ns.category}</span>
              <ul style={{ margin: '4px 0 0 16px', fontSize: 12, color: '#475569' }}>
                {(ns.items || []).map((item, j) => <li key={j}>{item}</li>)}
              </ul>
            </div>
          ))}
        </div>
      )}

      {docsReviewed.length > 0 && (
        <div className="pi-card">
          <h4 style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Documents Reviewed ({docsReviewed.length})</h4>
          <div style={{ fontSize: 12, color: '#475569' }}>
            {docsReviewed.map((d, i) => (
              <div key={i} style={{ padding: '3px 0', borderBottom: '1px solid #f1f5f9' }}>
                <span style={{ color: '#94a3b8', marginRight: 8 }}>{d.category}</span>
                {d.document}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PIDDReferenceTab({ portco }) {
  const [ddContext, setDdContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('report');
  const [filterSeverity, setFilterSeverity] = useState('all');

  useEffect(() => {
    piApi.getDDContext(portco.id)
      .then(r => setDdContext(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [portco.id]);

  if (loading) return (
    <div className="pi-loading" style={{ padding: 40 }}>
      <div className="pi-spinner" />
      <span>Loading DD artifacts...</span>
    </div>
  );

  if (!ddContext?.has_dd_artifacts) return (
    <div className="pi-empty" style={{ padding: 60 }}>
      <FileText size={32} color="#94a3b8" />
      <div className="pi-empty-title">No Due Diligence Artifacts</div>
      <div className="pi-empty-sub">
        This portfolio company was not promoted from a deal with completed DD analysis,
        or the DD analysis has not been run yet.
      </div>
    </div>
  );

  const { dd_report, findings_report } = ddContext;
  const flags = findings_report?.flags || [];
  const filteredFlags = filterSeverity === 'all' ? flags : flags.filter(f => f.severity === filterSeverity);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Due Diligence Reference</h3>
          <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 0' }}>
            Read-only artifacts from the original deal diligence.
            {ddContext.deal_name && <> Source deal: <strong>{ddContext.deal_name}</strong></>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button className={`pi-tab-btn ${activeView === 'report' ? 'pi-tab-active' : ''}`}
            onClick={() => setActiveView('report')}>
            <FileText size={13} style={{ marginRight: 4 }} /> DD Report
          </button>
          <button className={`pi-tab-btn ${activeView === 'findings' ? 'pi-tab-active' : ''}`}
            onClick={() => setActiveView('findings')}>
            <AlertTriangle size={13} style={{ marginRight: 4 }} /> Findings ({flags.length})
          </button>
        </div>
      </div>

      {activeView === 'report' && <DDReportSection report={dd_report} />}

      {activeView === 'findings' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <SeveritySummary report={findings_report} />
            <select value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)}
              style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 4 }}>
              <option value="all">All Severities</option>
              <option value="CRITICAL">Critical</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </select>
          </div>

          {filteredFlags.length === 0
            ? <div className="pi-empty-sub">No findings match the selected filter.</div>
            : filteredFlags.map((f, i) => <FlagCard key={i} flag={f} />)
          }
        </div>
      )}
    </div>
  );
}
